import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { Link } from 'react-router-dom';
import { Plus, Building2, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    billing_contact_name: '',
    billing_email: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients`);
      const data = await res.json();
      setClients(data.clients || []);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setFormData({ company_name: '', billing_contact_name: '', billing_email: '' });
        setShowForm(false);
        fetchClients();
      }
    } catch (err) {
      console.error('Failed to create client:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const confirmMsg = 'Delete this client?\n\nThis will PERMANENTLY DELETE the client and ALL associated sites and contracts.\n\nThis action cannot be undone.';
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}?force=true`, { method: 'DELETE' });
      if (res.ok) {
        const data = await res.json();
        if (data.deleted_contracts > 0) {
          alert(`Client deleted along with ${data.deleted_contracts} contract(s).`);
        }
        fetchClients();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to delete client');
      }
    } catch (err) {
      console.error('Failed to delete client:', err);
      alert('Failed to delete client. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Clients</h1>
          <p className="text-gray-500 text-sm mt-1">{clients.length} client{clients.length !== 1 ? 's' : ''}</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#1a1f4e] hover:bg-[#2a2f5e]"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Add Client Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">New Client</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <input
                type="text"
                required
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none"
                data-testid="client-company-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Contact Name *</label>
                <input
                  type="text"
                  required
                  value={formData.billing_contact_name}
                  onChange={(e) => setFormData({ ...formData, billing_contact_name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none"
                  data-testid="client-contact-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Email *</label>
                <input
                  type="email"
                  required
                  value={formData.billing_email}
                  onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none"
                  data-testid="client-email-input"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={saving} className="bg-[#1a1f4e] hover:bg-[#2a2f5e]" data-testid="save-client-btn">
                {saving ? 'Saving...' : 'Save Client'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {/* Client List */}
      <div className="space-y-3">
        {clients.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No clients yet. Add your first client to get started.</p>
          </div>
        ) : (
          clients.map((client) => (
            <div key={client.id} className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
              <div className="flex items-center justify-between p-4">
                <Link to={`/admin/clients/${client.id}`} className="flex items-center gap-4 flex-1">
                  <div className="w-10 h-10 bg-[#1a1f4e]/10 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#1a1f4e]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{client.company_name}</p>
                    <p className="text-sm text-gray-500">
                      {client.billing_contact_name} · {client.billing_email}
                      {client.invoice_cc_email && <span className="ml-2 text-blue-500">(CC: {client.invoice_cc_email})</span>}
                    </p>
                  </div>
                </Link>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => handleDelete(client.id, e)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete client and all associated data"
                    data-testid={`delete-client-${client.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <Link to={`/admin/clients/${client.id}`}>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </Link>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ClientList;
